// One-time migration from the pre-split `connect-token` store (a single grab-bag) to the three
// concern-scoped stores: connect-credentials, app-session, session-meta. The legacy store persisted
// every field under one localStorage key; this seeds each new key from that blob and removes the old
// one, so an open session is adopted on the deploy that introduces the split instead of being orphaned.

const LEGACY_KEY = 'connect-token';

interface PersistedEnvelope {
    state?: Record<string, unknown>;
}

function seedIfAbsent(key: string, state: Record<string, unknown>): void {
    if (localStorage.getItem(key) === null) {
        // Written at version 0 — the legacy blob predates every store migration, so each store's
        // own `migrate` must still run over it on hydrate.
        localStorage.setItem(key, JSON.stringify({ state, version: 0 }));
    }
}

/**
 * Splits the legacy `connect-token` blob across the three new store keys, then removes it.
 * Idempotent and safe to call when no legacy blob exists. Exported (unguarded) for testing.
 */
export function migrateLegacyConnectToken(): void {
    if (typeof localStorage === 'undefined') return;
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (legacyRaw === null) return;

    try {
        const legacy = JSON.parse(legacyRaw) as PersistedEnvelope;
        const s = legacy.state ?? {};

        seedIfAbsent('connect-credentials', {
            // Tolerate a pre-rename legacy blob (`token`) as well as the renamed `acceoConnectToken`.
            acceoConnectToken: s.acceoConnectToken ?? s.token ?? null,
            expiresAt: s.expiresAt ?? null,
            saasSessionToken: s.saasSessionToken ?? null,
            portalIsToken: s.portalIsToken ?? null,
        });
        seedIfAbsent('app-session', {
            appAccessToken: s.appAccessToken ?? null,
            appRefreshToken: s.appRefreshToken ?? null,
            appAccessExpiresAt: s.appAccessExpiresAt ?? null,
        });
        // sessionKind only — the recovery flags describe an in-flight re-nonce and are deliberately
        // not persisted by `session-meta`, so seeding them would just be stripped again on hydrate.
        seedIfAbsent('session-meta', {
            sessionKind: s.sessionKind ?? null,
        });

        localStorage.removeItem(LEGACY_KEY);
    } catch {
        // Malformed legacy blob — drop it; the new stores fall back to their defaults.
        // removeItem itself can throw in storage-restricted environments (Safari private mode,
        // quota-exceeded state), so swallow that too — the old key simply stays until evicted.
        try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignored */ }
    }
}

let migrated = false;

/**
 * Runs {@link migrateLegacyConnectToken} exactly once per page load. Each split-store module calls
 * this before creating its persisted store, so the legacy blob is split before any of them hydrates —
 * regardless of which store module the bundler evaluates first.
 */
export function migrateLegacyConnectTokenOnce(): void {
    if (migrated) return;
    migrated = true;
    migrateLegacyConnectToken();
}
